import React, { useCallback, useEffect, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import {
    getManageUsersUrl,
    getManageUserUrl,
    getUserSubscriptionActionUrl,
    getUserForceLogoutUrl,
    getUserRefundUrl,
    getUserMessageUrl,
    getUserDeleteUrl,
} from '../../utils/UrlUtilities';
import {
    constructUserUpdatePayload,
    constructAdminSubscriptionPayload,
    constructRefundPayload,
    constructUserMessagePayload,
    constructUserDeletePayload,
} from '../../utils/PayloadUtils';
import { PLAN_CODES, PLAN_LABELS, itemsPerPageOptions } from '../../utils/Constants';
import { formatBdPst, formatPst } from '../../utils/DateUtilities';
import { isEmpty } from '../../utils/BasicUtilities';
import CloseButton from '../ui/CloseButton';

const ROLE_OPTIONS = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' },
    { value: 'super_admin', label: 'Super Admin' },
];
const PLAN_OPTIONS = Object.values(PLAN_CODES).filter((c) => c !== PLAN_CODES.FREE);
const STATUS_BADGE = { SUCCESS: 'bg-success', ACTIVE: 'bg-success', PENDING: 'bg-warning text-dark', TRIAL: 'bg-info text-dark', CANCELLED: 'bg-warning text-dark', FAILED: 'bg-danger', EXPIRED: 'bg-secondary', REFUNDED: 'bg-secondary' };

// UTC ISO -> datetime-local (admin's wall clock), stable round-trip with new Date().toISOString().
function toDatetimeLocal(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function UserManagement() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();

    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState({ q: '', role: '', is_active: '' });
    const [applied, setApplied] = useState({ q: '', role: '', is_active: '' });

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    // Per-section form state (reset when a user is opened).
    const [profile, setProfile] = useState({ name: '', phone: '', institute: '' });
    const [subPlan, setSubPlan] = useState('');
    const [subExpiry, setSubExpiry] = useState('');
    const [refund, setRefund] = useState({ amount: '', phone: '', reason: '', cancelSub: false, sendEmail: false, subject: '', body: '' });
    const [refundConfirm, setRefundConfirm] = useState(false);
    const [message, setMessage] = useState({ subject: '', body: '' });
    const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        const params = { page, limit };
        if (!isEmpty(applied.q)) params.q = applied.q;
        if (!isEmpty(applied.role)) params.role = applied.role;
        if (!isEmpty(applied.is_active)) params.is_active = applied.is_active;
        const res = await CallApi({ url: getManageUsersUrl(params), method: 'GET', token: true });
        if (res.success) { setUsers(res.data.users || []); setTotal(res.data.total || 0); }
        setLoading(false);
    }, [page, limit, applied, CallApi]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const openUser = async (id) => {
        setDetailLoading(true);
        setDetail({ id });
        const res = await CallApi({ url: getManageUserUrl(id), method: 'GET', token: true });
        setDetailLoading(false);
        if (res.success) {
            const d = res.data;
            setDetail(d);
            setProfile({ name: d.name || '', phone: d.phone || '', institute: d.institute || '' });
            setSubPlan(''); setSubExpiry('');
            setRefund({ amount: '', phone: d.phone || '', reason: '', cancelSub: false, sendEmail: false, subject: '', body: '' });
            setMessage({ subject: '', body: `Hi ${d.name || 'there'},\n\n\n\n— Kobra Prep Team` });
            setDeleteConfirmEmail('');
        }
    };
    const closeUser = () => setDetail(null);

    // Generic action → refresh detail (endpoints return the updated UserDetailOut).
    const runDetail = async (fn, okMsg) => {
        setBusy(true);
        const res = await fn();
        setBusy(false);
        if (res.success) {
            if (res.data && res.data.id) setDetail(res.data);
            if (okMsg) showSuccess(okMsg);
            fetchUsers();
        }
        return res;
    };

    const saveProfile = () => runDetail(
        () => CallApi({ url: getManageUserUrl(detail.id), method: 'PUT', payload: constructUserUpdatePayload(profile), token: true }),
        'Profile updated.'
    );
    const toggleActive = () => runDetail(
        () => CallApi({ url: getManageUserUrl(detail.id), method: 'PUT', payload: constructUserUpdatePayload({ isActive: !detail.is_active }), token: true }),
        detail.is_active ? 'User deactivated.' : 'User activated.'
    );
    // Force logout: revoke all the user's tokens so they're signed out everywhere immediately.
    const forceLogout = () => runDetail(
        () => CallApi({ url: getUserForceLogoutUrl(detail.id), method: 'POST', token: true }),
        'User has been logged out.'
    );
    const setPlan = () => {
        if (!subPlan || !subExpiry) return;
        runDetail(
            () => CallApi({ url: getUserSubscriptionActionUrl(detail.id), method: 'POST', payload: constructAdminSubscriptionPayload({ action: 'set', planCode: subPlan, expiresAt: subExpiry }), token: true }),
            'Subscription set.'
        );
    };
    const editExpiry = (subId, expiresAt) => runDetail(
        () => CallApi({ url: getUserSubscriptionActionUrl(detail.id), method: 'POST', payload: constructAdminSubscriptionPayload({ action: 'edit_expiry', subscriptionId: subId, expiresAt }), token: true }),
        'Expiry updated.'
    );
    const cancelSub = () => runDetail(
        () => CallApi({ url: getUserSubscriptionActionUrl(detail.id), method: 'POST', payload: constructAdminSubscriptionPayload({ action: 'cancel' }), token: true }),
        'Subscription cancelled.'
    );
    // Delete subscription → downgrade to FREE now (expires all coverage; keeps history rows).
    const downgradeSub = () => runDetail(
        () => CallApi({ url: getUserSubscriptionActionUrl(detail.id), method: 'POST', payload: constructAdminSubscriptionPayload({ action: 'downgrade' }), token: true }),
        'Subscription deleted — user downgraded to Free.'
    );
    const doRefund = () => {
        setRefundConfirm(false);
        runDetail(
            () => CallApi({ url: getUserRefundUrl(detail.id), method: 'POST', payload: constructRefundPayload({ amountBdt: refund.amount, recipientPhone: refund.phone, reason: refund.reason, cancelSubscription: refund.cancelSub, sendEmail: refund.sendEmail, emailSubject: refund.subject, emailBody: refund.body }), token: true }),
            'Refund issued.'
        ).then((res) => { if (res.success) setRefund({ amount: '', phone: detail.phone || '', reason: '', cancelSub: false, sendEmail: false, subject: '', body: '' }); });
    };
    const sendMessage = () => runDetail(
        () => CallApi({ url: getUserMessageUrl(detail.id), method: 'POST', payload: constructUserMessagePayload(message.subject, message.body), token: true }),
        'Message sent.'
    );
    const doDelete = async (scope) => {
        const labels = { tokens: 'clear login/session tokens for', subscription: 'delete ALL subscription & billing data for', full: 'PERMANENTLY DELETE the account of' };
        if (!window.confirm(`Are you sure you want to ${labels[scope]} ${detail.email}?`)) return;
        setBusy(true);
        const res = await CallApi({ url: getUserDeleteUrl(detail.id), method: 'POST', payload: constructUserDeletePayload(scope, deleteConfirmEmail), token: true });
        setBusy(false);
        if (res.success) {
            showSuccess(res.data?.detail || 'Done.');
            if (scope === 'full') { closeUser(); } else { openUser(detail.id); }
            fetchUsers();
        }
    };

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-4" style={{ color: '#4c1d95' }}>User Management</h2>

            {/* Filters */}
            <div className="card mb-3 kp-filter"><div className="card-body row g-2 align-items-end">
                <div className="col-12 col-md-5">
                    <label className="form-label small mb-1">Search</label>
                    <input className="form-control form-control-sm" value={pending.q} onChange={(e) => setPending({ ...pending, q: e.target.value })} placeholder="name / email / phone" />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Role</label>
                    <select className="form-select form-select-sm" value={pending.role} onChange={(e) => setPending({ ...pending, role: e.target.value })}>
                        <option value="">All</option>
                        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Status</label>
                    <select className="form-select form-select-sm" value={pending.is_active} onChange={(e) => setPending({ ...pending, is_active: e.target.value })}>
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={() => { setApplied(pending); setPage(1); }}>Filter</button>
                </div>
            </div></div>

            {/* Grid */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0">
                    <thead className="table-light">
                        <tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Plan</th><th>Status</th><th>Logged in?</th><th>Last Login (PST)</th><th>Last Logout (PST)</th><th className="text-end">Actions</th></tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={10} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan={10} className="text-center text-muted py-4">No users found.</td></tr>
                        ) : users.map((u) => (
                            <tr key={u.id}>
                                <td>{u.name}</td>
                                <td className="small">{u.email}</td>
                                <td className="small">{u.phone || '—'}</td>
                                <td className="small text-uppercase">{u.role}</td>
                                <td className="small">{PLAN_LABELS[u.current_plan_code] || u.current_plan_code}</td>
                                <td>{u.is_active ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span>}</td>
                                <td>{u.is_logged_in ? <span className="badge bg-success">Yes</span> : <span className="badge bg-light text-dark border">No</span>}</td>
                                <td className="small">{formatPst(u.last_login)}</td>
                                <td className="small">{formatPst(u.last_logout)}</td>
                                <td className="text-end"><button className="btn btn-sm btn-outline-primary" onClick={() => openUser(u.id)}>Manage</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div></div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <label className="small mb-0">Items per page:</label>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                        {itemsPerPageOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span className="small text-muted">Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
            </div>

            {/* Detail modal */}
            {detail && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1050, overflowY: 'auto', padding: '2rem 0.5rem' }}>
                    <div className="card" style={{ width: '96%', maxWidth: 'min(1200px, 96vw)' }}>
                        <div className="card-header d-flex justify-content-between align-items-center fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                            <span>Manage user{detail.email ? ` — ${detail.email}` : ''}</span>
                            <CloseButton onClick={closeUser} />
                        </div>
                        <div className="card-body">
                            {detailLoading || !detail.email ? (
                                <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>
                            ) : (
                                <>
                                    {/* Profile */}
                                    <h6 className="fw-bold">Profile</h6>
                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-md-4"><label className="form-label small mb-0">Name</label><input className="form-control form-control-sm" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
                                        <div className="col-6 col-md-4"><label className="form-label small mb-0">Phone</label><input className="form-control form-control-sm" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
                                        <div className="col-6 col-md-4"><label className="form-label small mb-0">Institute</label><input className="form-control form-control-sm" value={profile.institute} onChange={(e) => setProfile({ ...profile, institute: e.target.value })} /></div>
                                    </div>
                                    <div className="small text-muted mb-2">
                                        Role: <strong>{detail.role}</strong> · Verified: {detail.email_verified ? 'yes' : 'no'} · {detail.submissions_count} submissions · Joined {formatBdPst(detail.created_at, { withTime: false })} · Last login {detail.last_login ? formatBdPst(detail.last_login) : '—'}
                                    </div>
                                    <div className="d-flex gap-2 mb-3">
                                        <button className="btn btn-sm btn-primary" onClick={saveProfile} disabled={busy}>Save profile</button>
                                        <button className={`btn btn-sm ${detail.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`} onClick={toggleActive} disabled={busy}>
                                            {detail.is_active ? 'Deactivate account' : 'Activate account'}
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={forceLogout} disabled={busy} title="Revoke all sessions — signs the user out everywhere">Force logout</button>
                                        <span className="ms-auto align-self-center">{detail.is_active ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span>}</span>
                                    </div>

                                    {/* Subscription */}
                                    <hr />
                                    <h6 className="fw-bold">Subscription <span className="badge bg-light text-dark ms-1">{PLAN_LABELS[detail.current_plan_code] || detail.current_plan_code}{detail.days_remaining > 0 ? ` · ${detail.days_remaining}d left` : ''}</span></h6>
                                    <div className="row g-2 align-items-end mb-2">
                                        <div className="col-6 col-md-4"><label className="form-label small mb-0">Set plan</label>
                                            <select className="form-select form-select-sm" value={subPlan} onChange={(e) => setSubPlan(e.target.value)}>
                                                <option value="">—</option>
                                                {PLAN_OPTIONS.map((c) => <option key={c} value={c}>{PLAN_LABELS[c]}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-6 col-md-4"><label className="form-label small mb-0">Expires</label><input type="datetime-local" className="form-control form-control-sm" value={subExpiry} onChange={(e) => setSubExpiry(e.target.value)} /></div>
                                        <div className="col-12 col-md-4 d-flex flex-wrap gap-2">
                                            <button className="btn btn-sm btn-primary" onClick={setPlan} disabled={busy || !subPlan || !subExpiry}>Set / change plan</button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={cancelSub} disabled={busy}>Cancel current subscription</button>
                                            <button className="btn btn-sm btn-danger" onClick={downgradeSub} disabled={busy} title="Expire all coverage now and drop the user to the Free plan">Delete subscription</button>
                                        </div>
                                    </div>
                                    <div className="table-responsive mb-3">
                                        <table className="table table-sm mb-0">
                                            <thead className="table-light"><tr><th>Plan</th><th>Status</th><th>Source</th><th>Expires</th><th></th></tr></thead>
                                            <tbody>
                                                {detail.subscriptions.length === 0 ? (
                                                    <tr><td colSpan={5} className="text-muted small">No subscriptions.</td></tr>
                                                ) : detail.subscriptions.map((s) => (
                                                    <tr key={s.id}>
                                                        <td className="small">{PLAN_LABELS[s.plan_code] || s.plan_code}</td>
                                                        <td><span className={`badge ${STATUS_BADGE[s.status] || 'bg-secondary'}`}>{s.status}</span></td>
                                                        <td className="small">{s.source}</td>
                                                        <td className="small">{formatBdPst(s.expires_at, { withTime: false })}</td>
                                                        <td className="text-end">
                                                            <input type="datetime-local" className="form-control form-control-sm d-inline-block" style={{ width: 200 }} defaultValue={toDatetimeLocal(s.expires_at)}
                                                                onChange={(e) => { s._newExpiry = e.target.value; }} />
                                                            <button className="btn btn-sm btn-outline-secondary ms-1" disabled={busy} onClick={() => s._newExpiry && editExpiry(s.id, new Date(s._newExpiry).toISOString())}>Save</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Refund */}
                                    <hr />
                                    <h6 className="fw-bold">Refund</h6>
                                    <div className="row g-2 align-items-end">
                                        <div className="col-6 col-md-3"><label className="form-label small mb-0">Amount (৳)</label><input type="number" min="1" className="form-control form-control-sm" value={refund.amount} onChange={(e) => setRefund({ ...refund, amount: e.target.value })} /></div>
                                        <div className="col-6 col-md-4"><label className="form-label small mb-0">bKash number</label><input className="form-control form-control-sm" value={refund.phone} onChange={(e) => setRefund({ ...refund, phone: e.target.value })} placeholder="+8801XXXXXXXXX" /></div>
                                        <div className="col-12 col-md-5"><label className="form-label small mb-0">Reason</label><input className="form-control form-control-sm" value={refund.reason} onChange={(e) => setRefund({ ...refund, reason: e.target.value })} /></div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-3 mt-2">
                                        <div className="form-check"><input className="form-check-input" type="checkbox" id="rfCancel" checked={refund.cancelSub} onChange={(e) => setRefund({ ...refund, cancelSub: e.target.checked })} /><label className="form-check-label small" htmlFor="rfCancel">Also cancel subscription</label></div>
                                        <div className="form-check"><input className="form-check-input" type="checkbox" id="rfEmail" checked={refund.sendEmail} onChange={(e) => setRefund({ ...refund, sendEmail: e.target.checked })} /><label className="form-check-label small" htmlFor="rfEmail">Email the user (from support@)</label></div>
                                    </div>
                                    <button className="btn btn-sm btn-warning mt-2" onClick={() => setRefundConfirm(true)} disabled={busy || !(Number(refund.amount) > 0)}>Issue refund</button>

                                    {/* Message */}
                                    <hr />
                                    <h6 className="fw-bold">Message the user <span className="small text-muted fw-normal">(from support@kobraprep.com)</span></h6>
                                    <input className="form-control form-control-sm mb-2" placeholder="Subject" value={message.subject} onChange={(e) => setMessage({ ...message, subject: e.target.value })} />
                                    <textarea className="form-control form-control-sm" rows={4} value={message.body} onChange={(e) => setMessage({ ...message, body: e.target.value })} />
                                    <button className="btn btn-sm btn-primary mt-2" onClick={sendMessage} disabled={busy || isEmpty(message.subject)}>Send email</button>
                                    {detail.messages.length > 0 && (
                                        <div className="small text-muted mt-2">Last message: <strong>{detail.messages[0].status}</strong> — {detail.messages[0].subject} ({detail.messages.length} total)</div>
                                    )}

                                    {/* Danger zone */}
                                    <hr />
                                    <h6 className="fw-bold text-danger">Danger zone</h6>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => doDelete('tokens')} disabled={busy}>Clear login/tokens</button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => doDelete('subscription')} disabled={busy}>Delete subscription &amp; billing</button>
                                    </div>
                                    <div className="border border-danger rounded p-2">
                                        <div className="small text-danger mb-1">Full account delete is permanent. Type the user's email to enable.</div>
                                        <div className="d-flex gap-2">
                                            <input className="form-control form-control-sm" placeholder={detail.email} value={deleteConfirmEmail} onChange={(e) => setDeleteConfirmEmail(e.target.value)} />
                                            <button className="btn btn-sm btn-danger" disabled={busy || deleteConfirmEmail.trim().toLowerCase() !== detail.email.toLowerCase()} onClick={() => doDelete('full')}>Delete account</button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Refund confirm */}
            {refundConfirm && detail && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(460px, 94vw)' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>Confirm refund</div>
                        <div className="card-body">
                            <p className="mb-1">Refund <strong>৳{refund.amount || 0}</strong> to <strong>{detail.name}</strong> (bKash {refund.phone || '—'})?</p>
                            {refund.cancelSub && <p className="small text-warning mb-1">Their subscription will also be cancelled.</p>}
                            {refund.sendEmail && <p className="small text-muted mb-0">A refund email will be sent to {detail.email}.</p>}
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setRefundConfirm(false)} disabled={busy}>Cancel</button>
                            <button className="btn btn-warning" onClick={doRefund} disabled={busy}>{busy ? 'Processing…' : 'Yes, refund'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
